import type {ApiResponse} from '@/types/api';

const API_BASE_URL = '/api';
const BETTER_AUTH_BASE_URL = '/api/auth';

export type ApiErrorCode =
    | 'UNAUTHORIZED'
    | 'FORBIDDEN'
    | 'NOT_FOUND'
    | 'VALIDATION_ERROR'
    | 'RATE_LIMIT_EXCEEDED'
    | 'NETWORK_ERROR'
    | 'UNKNOWN_ERROR'
    | string; // Allow other codes from server

export class ApiClientError extends Error {
    constructor(
        message: string,
        public code: ApiErrorCode,
        public details?: Array<{ field: string; message: string }>,
        public status?: number
    ) {
        super(message);
        this.name = 'ApiClientError';
    }
}

/**
 * BetterAuth answers with its own shape, not the control plane's {success, data}
 * envelope, so it needs its own caller. The session travels as a same-origin
 * cookie, which fetch sends by default.
 */
export async function betterAuthRequest<T = unknown>(
    endpoint: string,
    body?: unknown
): Promise<T> {
    let response: Response;

    try {
        response = await fetch(`${BETTER_AUTH_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body ?? {})
        });
    } catch {
        throw new ApiClientError(
            'Network error. Please check your connection.',
            'NETWORK_ERROR'
        );
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new ApiClientError(
            data?.message || 'Authentication failed',
            data?.code || 'UNAUTHORIZED',
            undefined,
            response.status
        );
    }

    return data as T;
}

export async function apiRequest<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const {headers = {}, ...fetchOptions} = options;

    const requestHeaders = new Headers(headers);
    if (!(options.body instanceof FormData) && !requestHeaders.has('Content-Type')) {
        requestHeaders.set('Content-Type', 'application/json');
    }
    const config: RequestInit = {
        ...fetchOptions,
        headers: requestHeaders
    };

    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

        const data: ApiResponse<T> = await response.json();

        if (response.status === 401) {
            const isAuthEndpoint = endpoint.startsWith('/auth/');
            if (!isAuthEndpoint) {
                const returnTo = `${window.location.pathname}${window.location.search}`;
                window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
            }
            const errorMessage = data.success === false && data.error?.message
                ? data.error.message
                : 'Invalid email or password';
            throw new ApiClientError(errorMessage, 'UNAUTHORIZED', undefined, response.status);
        }

        if (!response.ok || !data.success) {
            const errorInfo = data.success === false && data.error
                ? data.error
                : {code: 'UNKNOWN_ERROR' as const, message: 'An unexpected error occurred'};

            throw new ApiClientError(
                errorInfo.message,
                errorInfo.code,
                'details' in errorInfo ? errorInfo.details : undefined,
                response.status
            );
        }

        return data.data;
    } catch (error) {
        if (error instanceof ApiClientError) {
            throw error;
        }

        if (error instanceof TypeError) {
            throw new ApiClientError(
                'Network error. Please check your connection.',
                'NETWORK_ERROR'
            );
        }

        throw new ApiClientError(
            'An unexpected error occurred',
            'UNKNOWN_ERROR'
        );
    }
}
